/**
 * MaterialListSourcePicker — small modal shown when the user taps
 * the "Add material list" button in the Daily Plan editor.
 *
 * Two paths:
 *   1. "Ny liste" — enter a title, create a fresh list from scratch
 *      (matches the pre-existing `createMaterialList` behavior).
 *   2. "Fra kollega" — pick an existing progress-source material
 *      list posted by a colleague. The picker fetches every list
 *      tagged `source=progress` (optionally filtered by project) and
 *      returns its id so the editor can anchor to it.
 *
 * Access control is enforced server-side (`PUT /material-lists/{id}`
 * returns 403 for non-authors on progress-sourced lists), so this
 * component doesn't need to know who's who — it just shows the list
 * and lets the caller wire the outcome.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Package, User, Plus, Loader2, Calendar, Folder } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDay(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("no-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 10); }
}

export default function MaterialListSourcePicker({
  open,
  projectId,
  authHeader,
  onCancel,
  onCreated,     // (list) => void   — after creating a fresh list
  onPickExisting, // (list) => void  — after picking an existing progress list
}) {
  const [tab, setTab] = useState("new");
  const [title, setTitle] = useState("Listă materiale");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || "");

  useEffect(() => {
    if (!open) return;
    // Reset when reopening.
    setTitle("Listă materiale");
    setTab("new");
    setExisting([]);
    setSelectedProject(projectId || "");
    // Prefetch existing progress-source lists so the second tab is
    // instant once the user switches to it.
    (async () => {
      setLoading(true);
      try {
        const url = projectId
          ? `${API}/doserne/projects/${projectId}/material-lists`
          : null;
        if (url) {
          const { data } = await axios.get(url, authHeader());
          const flat = (data.days || []).flatMap((d) => d.lists || []);
          setExisting(flat.filter((l) => l.source === "progress"));
        } else {
          setExisting([]);
        }
      } catch { /* silent — nothing to show */ }
      setLoading(false);
    })();
    // A project is mandatory — if the plan didn't provide one, load the list
    // so the worker can pick it before posting.
    if (!projectId) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/doserne/projects`, authHeader());
          setProjects(data.projects || []);
        } catch { /* silent */ }
      })();
    }
  }, [open, projectId, authHeader]);

  const createNew = async () => {
    const t = title.trim();
    if (!t) { toast.error("Titlu mangler"); return; }
    const pid = projectId || selectedProject;
    if (!pid) { toast.error("Alege un proiect pentru listă"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/doserne/material-lists`,
        { plan_id: "", project_id: pid, title: t, source: "plan" },
        authHeader(),
      );
      onCreated?.(data);
    } catch {
      toast.error("Nu am putut crea lista");
    }
    setBusy(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onCancel}>
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85dvh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="material-list-source-picker"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center" style={{ color: "#fff" }}>
              <Package className="w-4 h-4" />
            </div>
            <div>
              <div className="text-base font-black text-slate-900">Materialliste</div>
              <div className="text-xs text-slate-500">Ny eller fra kollegas progresjon</div>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-3 flex gap-2 border-b border-slate-100">
          <button
            onClick={() => setTab("new")}
            className={`px-3 py-2 text-xs font-black uppercase tracking-wider ${tab === "new" ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"}`}
            data-testid="picker-tab-new"
          >
            Ny liste
          </button>
          <button
            onClick={() => setTab("existing")}
            className={`px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5 ${tab === "existing" ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"}`}
            data-testid="picker-tab-existing"
          >
            Fra kollega
            {existing.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px]">{existing.length}</span>
            )}
          </button>
        </div>

        {tab === "new" && (
          <div className="p-5 space-y-3" data-testid="picker-pane-new">
            {!projectId && (
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Proiect *</label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                  data-testid="picker-project-select"
                >
                  <option value="">— Alege proiect —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.number ? `${p.number} · ` : ""}{p.name || p.address || p.id}</option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">Lista trebuie legată de un proiect.</div>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Tittel</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createNew()}
                autoFocus
                maxLength={120}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                data-testid="picker-new-title"
              />
              <div className="text-[11px] text-slate-500 mt-1">Ex: «Team Klofta – tirsdag».</div>
            </div>
            <button
              onClick={createNew}
              disabled={busy || !title.trim() || (!projectId && !selectedProject)}
              className="w-full py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-sm font-black inline-flex items-center justify-center gap-2"
              style={{ color: "#fff" }}
              data-testid="picker-create-new"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Opprett tom liste
            </button>
          </div>
        )}

        {tab === "existing" && (
          <div className="flex-1 overflow-y-auto p-3" data-testid="picker-pane-existing">
            {!projectId && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                <Folder className="w-4 h-4" />
                Legg til et prosjekt i notatet først for å se lister fra kolleger.
              </div>
            )}
            {projectId && loading && (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            )}
            {projectId && !loading && existing.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">
                Ingen kolleger har postet en materialliste for dette prosjektet enda.
              </div>
            )}
            <ul className="space-y-2">
              {existing.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => onPickExisting?.(l)}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 active:bg-emerald-100 transition"
                    data-testid={`picker-existing-${l.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-black text-slate-900 truncate">{l.title}</div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{l.created_by_name || l.created_by}</span>
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDay(l.created_at)}</span>
                        </div>
                      </div>
                      <div className="text-[10px] font-black text-emerald-600 px-2 py-1 rounded-full bg-emerald-100">
                        {l.items?.length || 0} pos.
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
